import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Application } from 'pixi.js';
import { Live2DModel, Cubism4InternalModel } from 'pixi-live2d-display';
import { useTypewriter } from '../hooks/useTypewriter';
import { api } from '../api/client';
import { setToken } from '../utils/auth';
import styles from './Entry.module.css';
import { useTerminal } from '../hooks/useTerminal';
import type { TerminalCommand } from '../hooks/useTerminal';
import { Terminal } from '../components/Terminal/Terminal';
import { getItem, setItem } from '../utils/idb';

import { Icon } from '@ricons/utils';
import { FolderOpenOutline, DocumentOutline, FolderOutline, TerminalOutline } from '@ricons/ionicons5';

const LOG_LINES = [
  '> SYSTEM KERNEL v2.77 BOOTING...',
  '> ESTABLISHING SECURE CONNECTION...',
  '> LOADING MODULES: CORE | UI | DB',
  '> SYSTEM READY',
];

export default function Entry() {
  const navigate = useNavigate();
  const avatarCanvasRef = useRef<HTMLCanvasElement>(null);
  const logsRef = useRef<HTMLDivElement>(null);
  const [showCursor, setShowCursor] = useState(true);
  const [isAvatarLoading, setIsAvatarLoading] = useState(true);

  const lastDocsRef = useRef<{ slug: string, title: string }[]>([]);
  const customRoutes = ['/', '/admin', '/archive', '/about'];

  useEffect(() => {
    getItem<{ slug: string, title: string }[]>('terminal_docs').then(docs => {
      if (docs && Array.isArray(docs)) {
        lastDocsRef.current = docs;
      }
    });
  }, []);

  const commands: Record<string, TerminalCommand> = {
    login: {
      description: 'login <用户名> - 登录到系统',
      handler: async (args, ctx) => {
        const u = args[0] || 'ranxin';
        ctx.setUsername(u);
        const pwd = await ctx.requestPassword();
        ctx.print(`正在认证 ${u.toUpperCase()}...`, 'res');
        try {
          const res = await api.login(u, pwd);
          setToken(res.token, res.expires_at);
          ctx.print('访问被允许，正在重定向...', 'success');
          setTimeout(() => navigate('/admin'), 800);
        } catch (e) {
          // 密码错误：从密码输入态回退到命令态，避免卡死在 psw 输入框
          ctx.setPhase('command');
          ctx.print('访问被拒绝，用户名或密码错误', 'err');
        }
      }
    },
    exit: {
      description: 'exit - 关闭终端页面',
      handler: (_args, ctx) => {
        ctx.print('正在终止会话...', 'dim');
        setTimeout(() => window.close(), 500);
      }
    },
    cd: {
      description: 'cd <路由> - 跳转到指定页面',
      completions: (args) => customRoutes.filter(r => r.startsWith(args[0] || '')),
      handler: (args, ctx) => {
        const route = args[0];
        if (!route) {
          ctx.print('用法: cd <路由>', 'err');
          return;
        }
        ctx.print(`正在导航至 ${route}...`, 'res');
        setTimeout(() => navigate(route), 300);
      }
    },
    help: {
      description: 'help - 查看所有可用命令',
      handler: (_args, ctx) => {
        const cmdKeys = Object.keys(commands).sort((a, b) => a.localeCompare(b));
        const cmdList = cmdKeys.map(k => `  ${commands[k].description}`);
        ctx.printLines([
          { text: '可用命令:', type: 'res' },
          ...cmdList.map(t => ({ text: t, type: 'res' as const }))
        ]);
      }
    },
    blog: {
      description: 'blog [序号|ID] - 查看最近文章或打开指定文章',
      handler: async (args, ctx) => {
        if (args[0]) {
          const target = args[0];
          let slug = target;
          if (!isNaN(Number(target))) {
            const index = Number(target) - 1;
            if (lastDocsRef.current[index]) {
              slug = lastDocsRef.current[index].slug;
            }
          }
          ctx.print(`正在打开文章 ${slug}...`, 'res');
          setTimeout(() => navigate(`/archive/${slug}`), 300);
        } else {
          ctx.print('正在获取最近文章...', 'dim');
          try {
            const docs = await api.listDocs({ status: 'published' });
            if (!docs || docs.length === 0) {
              ctx.print('未找到文章', 'dim');
              return;
            }
            const latest = docs.slice(0, 10);
            lastDocsRef.current = latest;
            setItem('terminal_docs', latest);
            ctx.printLines(latest.map((d, i) => ({
              text: `[${i + 1}] ${d.title} (${d.slug})`, type: 'res',
              onClick: () => navigate(`/archive/${d.slug}`)
            })));
            ctx.print('输入 "blog <序号>" 或直接输入序号来阅读', 'dim');
          } catch (e) {
            ctx.print('获取文章失败', 'err');
          }
        }
      }
    },
    search: {
      description: 'search <关键词> - 搜索文章',
      handler: async (args, ctx) => {
        const keyword = args.join(' ');
        if (!keyword) {
          ctx.print('用法: search <关键词>', 'err');
          return;
        }
        ctx.print(`正在搜索 "${keyword}"...`, 'dim');
        try {
          const docs = await api.listDocs({ q: keyword, status: 'published' });
          if (!docs || docs.length === 0) {
            ctx.print('未找到结果', 'dim');
            return;
          }
          const latest = docs.slice(0, 10);
          lastDocsRef.current = latest;
          setItem('terminal_docs', latest);
          ctx.printLines(latest.map((d, i) => ({
            text: `[${i + 1}] ${d.title} (${d.slug})`, type: 'res',
            onClick: () => navigate(`/archive/${d.slug}`)
          })));
          ctx.print('输入 "blog <序号>" 或直接输入序号来阅读', 'dim');
        } catch (e) {
           ctx.print('搜索失败', 'err');
        }
      }
    }
  };

  const terminalProps = useTerminal({
    bootLines: LOG_LINES,
    bootSpeed: 50,
    initialHistory: [],
    commands,
    onCommandNotFound: (cmd, ctx) => {
      if (!isNaN(Number(cmd)) && lastDocsRef.current.length > 0) {
        const index = Number(cmd) - 1;
        if (lastDocsRef.current[index]) {
          const slug = lastDocsRef.current[index].slug;
          ctx.print(`正在打开文章 ${slug}...`, 'res');
          setTimeout(() => navigate(`/archive/${slug}`), 300);
          return;
        }
      }
      ctx.print(`未知命令: ${cmd}`, 'err');
    }
  });

  const { phase, lineIndex } = terminalProps;
  const currentLine = useTypewriter(LOG_LINES[lineIndex] || '', 30);

  // Live2D Avatar Initialization
  useEffect(() => {
    let app: Application | null = null;
    let model: Live2DModel | null = null;
    let mounted = true;

    const initAvatar = async () => {
      const canvas = avatarCanvasRef.current;
      if (!canvas) return;

      const rect = canvas.parentElement?.getBoundingClientRect();
      const width = rect?.width || 300;
      const height = rect?.height || 400;

      app = new Application({
        view: canvas,
        width,
        height,
        backgroundAlpha: 0,
        autoStart: true,
      });

      try {
        model = await Live2DModel.from('/models/林翩翩/林翩翩.model3.json');
        if (!mounted) return;
        
        setIsAvatarLoading(false);
        app.stage.addChild(model as any);
        const m = model as any;
        
        const scale = Math.max(width / m.width, height / m.height) * 1.2;
        m.scale.set(scale);
        m.anchor.set(0.5, 1);
        m.x = width / 2;
        m.y = height + 10;

        const internal = model.internalModel as Cubism4InternalModel | undefined;
        if (internal) {
          const idx = internal.coreModel.getParameterIndex('Param56');
          internal.coreModel.setParameterValueByIndex(idx, 1);
        }

        const param55Idx = internal?.coreModel.getParameterIndex('Param55');
        let param55State = false;
        canvas.addEventListener('click', () => {
          if (!internal || param55Idx === undefined) return;
          param55State = !param55State;
          internal.coreModel.setParameterValueByIndex(param55Idx, param55State ? 1 : 0);
        });
        
      } catch (e) {
        console.error('Avatar model load error', e);
      }
    };

    initAvatar();

    return () => {
      mounted = false;
      model?.destroy();
      app?.destroy(false, { children: true });
    };
  }, []);

  // Auto-scroll logs
  useEffect(() => {
    if (logsRef.current) {
      logsRef.current.scrollTop = logsRef.current.scrollHeight;
    }
  }, [lineIndex, currentLine, phase]);

  // Blink cursor
  useEffect(() => {
    const interval = setInterval(() => setShowCursor((p) => !p), 500);
    return () => clearInterval(interval);
  }, []);

  const getTimeStr = (index: number) => {
      const seconds = index + 1;
      return `[08:42:${seconds.toString().padStart(2, '0')}]`;
  };

  return (
    <div className={styles.layout}>
      <main className={styles.mainGrid}>
        
        <div className={styles.leftCol}>
          <div className={`${styles.brutalPanel} ${styles.panelContent}`}>
            <div className={styles.panelHeader}>
              <h2 className={styles.panelTitle}>System Tree</h2>
              <div className={styles.panelSubtitle}>SYS.ARCHIVE</div>
            </div>
            <div className={styles.termScrollbar} style={{ overflowY: 'auto', flexGrow: 1, paddingRight: '8px' }}>
              <ul className={styles.treeList}>
                <li className={`${styles.treeItem} ${styles.active}`}>
                  <Icon size="18px"><FolderOpenOutline /></Icon>
                  <span>ROOT</span>
                </li>
                <li className={styles.treeSubList}>
                  <div className={styles.treeSubItem}>
                    <Icon size="16px"><DocumentOutline /></Icon>
                    <span>core_modules.sys</span>
                  </div>
                  <div className={styles.treeSubItem}>
                    <Icon size="16px"><DocumentOutline /></Icon>
                    <span>user_data.bin</span>
                  </div>
                  <div className={styles.treeSubItem}>
                    <Icon size="16px"><FolderOutline /></Icon>
                    <span>archives_old/</span>
                  </div>
                </li>
                <li className={styles.treeItem} style={{ marginTop: '0.75rem' }}>
                  <Icon size="18px"><FolderOutline /></Icon>
                  <span>NETWORK</span>
                </li>
              </ul>
            </div>
          </div>
          
          <div className={styles.brutalPanel} style={{ padding: '1.25rem', flexShrink: 0 }}>
            <div className={styles.panelHeader} style={{ paddingBottom: '0.75rem', marginBottom: '1rem' }}>
              <h3 className={styles.panelTitleSm}>Node Status</h3>
            </div>
            <div>
              <div className={styles.statRow}>
                <span className={styles.statLabel}>CPU_LOAD</span>
                <span className={styles.statValue}>42%</span>
              </div>
              <div className={styles.progressBar}>
                {[1, 2, 3, 4].map(i => <div key={`cpu-${i}`} className={`${styles.progressSegment} ${styles.filled}`}></div>)}
                {[1, 2, 3, 4, 5, 6].map(i => <div key={`cpu-e-${i}`} className={styles.progressSegment}></div>)}
              </div>
              <div className={styles.statRow} style={{ marginTop: '1rem' }}>
                <span className={styles.statLabel}>MEM_ALLOC</span>
                <span className={styles.statValue}>78%</span>
              </div>
              <div className={styles.progressBar}>
                {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <div key={`mem-${i}`} className={`${styles.progressSegment} ${styles.filled}`}></div>)}
                {[1, 2].map(i => <div key={`mem-e-${i}`} className={styles.progressSegment}></div>)}
              </div>
            </div>
          </div>
        </div>

        <div className={`${styles.centerCol} ${styles.brutalPanel}`}>
          <div className={styles.asciiContainer}>
            <pre className={styles.asciiArt}>
{[
  "██████╗  █████╗ ███╗   ██╗██╗  ██╗██╗███╗   ██╗",
  "██╔══██╗██╔══██╗████╗  ██║╚██╗██╔╝██║████╗  ██║",
  "██████╔╝███████║██╔██╗ ██║ ╚███╔╝ ██║██╔██╗ ██║",
  "██╔══██╗██╔══██║██║╚██╗██║ ██╔██╗ ██║██║╚██╗██║",
  "██║  ██║██║  ██║██║ ╚████║██╔╝ ██╗██║██║ ╚████║",
  "╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═══╝╚═╝  ╚═╝╚═╝╚═╝  ╚═══╝"
].join('\n')}
            </pre>
          </div>
          
          <Terminal {...terminalProps} />

        </div>

        <div className={styles.rightCol}>
          
          <div className={`${styles.avatarBox} ${styles.brutalPanelAccent}`}>
            <div className={styles.charBracketTl}></div>
            <div className={styles.charBracketTr}></div>
            <div className={styles.charBracketBl}></div>
            <div className={styles.charBracketBr}></div>
            
            <div className={styles.avatarId}>ID: 0x77A</div>
            <div className={styles.avatarStatus}>
              <div className={styles.statusDot}></div>
              <span className={styles.statusText}>{isAvatarLoading ? 'CONNECTING...' : 'ONLINE'}</span>
            </div>
            
            <div className={styles.avatarImageWrapper}>
              {isAvatarLoading && (
                <div className={styles.avatarLoadingOverlay}>
                  <div className={styles.glitchText}>LOADING_MODEL_DATA...</div>
                  <div className={styles.loadingBarContainer}>
                     <div className={styles.loadingBarFill}></div>
                  </div>
                </div>
              )}
              <canvas ref={avatarCanvasRef} className={styles.avatarImage} style={{ opacity: isAvatarLoading ? 0 : 1, transition: 'opacity 0.5s' }} />
              <div className={styles.avatarOverlay}></div>
              <div className={styles.avatarVersion}>
                <span className={styles.avatarVersionText}>v2.77</span>
              </div>
            </div>
          </div>

          <div className={`${styles.brutalPanel} ${styles.panelContent}`}>
            <div className={styles.panelHeader}>
              <h3 className={styles.panelTitleSm} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Icon size="16px"><TerminalOutline /></Icon>
                LIVE LOGS
              </h3>
              <span className={styles.badgeRec}>REC</span>
            </div>
            
            <div className={`${styles.logsContainer} ${styles.termScrollbar}`} ref={logsRef}>
              {LOG_LINES.slice(0, lineIndex).map((line, i) => (
                <div key={i} className={styles.logLine}>
                  <span className={styles.logTime}>{getTimeStr(i)}</span>
                  <span className={line.includes('LOCATED') || line.includes('READY') ? styles.logTextFixed : styles.logTextSurface}>
                    {line}
                  </span>
                </div>
              ))}
              
              {phase === 'boot' && lineIndex < LOG_LINES.length && (
                <div className={styles.logLine}>
                  <span className={styles.logTime}>{getTimeStr(lineIndex)}</span>
                  <span className={styles.logTextVariant}>
                    {currentLine}
                    <span className={showCursor ? '' : styles.logTextDim} style={{ color: 'var(--accent-cyan)' }}>█</span>
                  </span>
                </div>
              )}
              
              {phase !== 'boot' && (
                <>
                  <div className={styles.logLine}>
                    <span className={styles.logTime}>{getTimeStr(LOG_LINES.length)}</span>
                    <span className={styles.logTextFaded}>&gt; Waiting for user input...</span>
                  </div>
                  <div className={styles.logLine}>
                    <span className={styles.logTime}>{getTimeStr(LOG_LINES.length + 4)}</span>
                    <span className={styles.logTextDim}>&gt; Background tasks idled.</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

      </main>
      <div className={styles.icp}><a href="https://beian.miit.gov.cn/" target="_blank">粤ICP备2021149156号-1</a></div>
    </div>
  );
}
